/**
 * The organisation's record of cloud runs (roadmap D184; the workbench's
 * docs/plans/2026-10-cloud-agents.md, section 11), as the console reads it. AIOE-only: the wire
 * contract the workbench posts is cloudAgents.ts, mirrored from the workbench.
 *
 * Never task text, tool arguments or tool answers: who started a run, which provider, member and
 * task, how long it ran, what the provider reported (its task id, page and pull request) and how
 * each tool call went.
 */
import { z } from "zod";
import { cloudProviderIdSchema, cloudRunEventSchema } from "./cloudAgents.js";
/** `open` until the workbench ends it; an open run past its `expiresAt` reads as `expired`. */
export const cloudRunStateSchema = z.enum(["open", "ended", "expired"]);
/** One tool call relayed for a run: never its arguments or its answer. */
export const cloudRunCallSchema = z.object({
    at: z.string(),
    /** The MCP method: `initialize`, `tools/list`, `tools/call`, … */
    method: z.string(),
    /** For `tools/call`, the tool. */
    tool: z.string().optional(),
    /** `ok`; `error` (the workbench answered with an error); `unreachable` (the workbench was not connected). */
    outcome: z.enum(["ok", "error", "unreachable"]),
    ms: z.number().int().nonnegative(),
});
export const cloudRunSchema = z.object({
    id: z.string(),
    projectId: z.string().optional(),
    rootId: z.string().optional(),
    workspace: z.string().optional(),
    /** The workbench that started the run and owns its tools. */
    deviceId: z.string(),
    startedBy: z.string(),
    provider: cloudProviderIdSchema,
    team: z.string(),
    member: z.string(),
    task: z.string(),
    tools: z.array(z.string()),
    createdAt: z.string(),
    expiresAt: z.string(),
    state: cloudRunStateSchema,
    endedAt: z.string().optional(),
    endReason: z.string().optional(),
    /** Whether the provider was handed the organisation's MCP endpoint for the workbench's tools. */
    mcp: z.boolean(),
    /** The latest the provider reported, from the events. */
    providerTaskId: z.string().optional(),
    url: z.string().optional(),
    pullRequest: z.string().optional(),
    /** The newest last; at most 200 kept. */
    events: z.array(cloudRunEventSchema),
    /** Tool calls so far, and the newest 50 of them. */
    callCount: z.number().int().nonnegative(),
    calls: z.array(cloudRunCallSchema),
});
/** `GET /admin/v1/cloud-agents/runs`. */
export const cloudRunListSchema = z.object({ runs: z.array(cloudRunSchema), truncated: z.boolean() });
/** `POST /cloud-agents/v1/runs/:runId/end`. */
export const cloudRunEndSchema = z.object({ reason: z.string().max(200).optional() });
/** `POST /cloud-agents/v1/runs/:runId/events`. */
export const cloudRunEventsPostSchema = z.object({ events: z.array(cloudRunEventSchema).min(1).max(100) });
/** How long a run's credential may live, whatever the workbench asks for. */
export const CLOUD_RUN_MAX_HOURS = 4;
//# sourceMappingURL=cloudAgentsAdmin.js.map